"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useAuth } from "@/features/auth/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { Loading } from "@/components/ui/loading";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { addNewTalentProfile, getPartners } from "@/lib/supabase";

export interface AddNewTalentButtonProps {
  isApplicationList: boolean;
  onCreated?: () => void;
}

export function AddNewTalentButton({
  isApplicationList,
  onCreated
}: AddNewTalentButtonProps) {
  const { profile } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation(["applications", "auth", "common"]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [partners, setPartners] = useState<{ id: string; name: string }[]>([]);
  const [isLoadingPartners, setIsLoadingPartners] = useState(false);

  const formSchema = z
    .object({
      email: z.string().email(t("auth:errors.email_invalid")),
      password: z.string().min(6, t("auth:errors.password_too_short")),
      confirmPassword: z.string(),
      givenName: z.string().min(1, "First name is required"),
      familyName: z.string().min(1, "Last name is required"),
      partnerId: z.string().optional()
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("auth:errors.passwords_no_match"),
      path: ["confirmPassword"]
    });

  type FormData = z.infer<typeof formSchema>;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset
  } = useForm<FormData>({
    resolver: zodResolver(formSchema)
  });

  // Fetch partners when dialog opens
  useEffect(() => {
    if (isDialogOpen) {
      const fetchPartners = async () => {
        setIsLoadingPartners(true);
        try {
          const data = await getPartners();
          setPartners(data);
        } catch (error) {
          console.error("Failed to fetch partners", error);
          toast({
            title: t("common:status.error"),
            description: "Failed to load partners",
            variant: "destructive"
          });
        } finally {
          setIsLoadingPartners(false);
        }
      };
      fetchPartners();
    }
  }, [isDialogOpen, t, toast]);

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      await addNewTalentProfile(
        data.email,
        data.password,
        data.givenName,
        data.familyName,
        data.partnerId
      );
      toast({
        title: t("common:status.success"),
        description: "Talent user created successfully"
      });
      setIsDialogOpen(false);
      reset();
      onCreated?.();
    } catch (err) {
      console.error("Error creating talent:", err);
      toast({
        title: t("common:status.error"),
        description:
          err instanceof Error ? err.message : "Failed to create talent",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!profile.isSuperUser || isApplicationList) return null;

  return (
    <>
      <Button onClick={() => setIsDialogOpen(true)} variant="default">
        <Plus className="h-4 w-4 mr-2" />
        {t("messages.add_new_talent", { defaultValue: "Add new talent" })}
      </Button>
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("messages.add_new_talent", { defaultValue: "Add New Talent" })}
            </DialogTitle>
          </DialogHeader>
          {isLoadingPartners ? (
            <div className="py-8">
              <Loading size="sm" />
            </div>
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-4"
              autoComplete="off"
            >
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="off"
                  {...register("email")}
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <p className="text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  {...register("password")}
                  disabled={isSubmitting}
                />
                {errors.password && (
                  <p className="text-sm text-red-600">
                    {errors.password.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Password Confirmation *</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  {...register("confirmPassword")}
                  disabled={isSubmitting}
                />
                {errors.confirmPassword && (
                  <p className="text-sm text-red-600">
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="givenName">First Name *</Label>
                <Input
                  id="givenName"
                  autoComplete="given-name"
                  {...register("givenName")}
                  disabled={isSubmitting}
                />
                {errors.givenName && (
                  <p className="text-sm text-red-600">
                    {errors.givenName.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="familyName">Last Name *</Label>
                <Input
                  id="familyName"
                  autoComplete="family-name"
                  {...register("familyName")}
                  disabled={isSubmitting}
                />
                {errors.familyName && (
                  <p className="text-sm text-red-600">
                    {errors.familyName.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="partner">Partner</Label>
                <Select
                  onValueChange={(value) => setValue("partnerId", value)}
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a partner" />
                  </SelectTrigger>
                  <SelectContent>
                    {partners.map((partner) => (
                      <SelectItem key={partner.id} value={partner.id}>
                        {partner.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false);
                    reset();
                  }}
                  disabled={isSubmitting}
                >
                  {t("common:buttons.cancel")}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("common:status.saving")}
                    </>
                  ) : (
                    t("common:buttons.save")
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
